import { PackagePlus, Pencil, SlidersHorizontal, Tags } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@platform/auth-client';
import { Badge, Button, Dialog, formatMoney } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { nameIn } from './categories.ts';
import type { OptionIndex } from './options.ts';
import { PriceTag } from './PriceTag.tsx';
import { ProductGallery } from './ProductGallery.tsx';
import {
  COST_ROLE,
  firstVariant,
  formatMargin,
  marginOf,
  productStatusTone,
  type Product,
} from './product.ts';
import { variantRows } from './product-stock.ts';
import styles from './ProductQuickView.module.css';
import { QuickAddStockForm } from '../inventory/QuickAddStockForm.tsx';
import { VariantStock } from '../inventory/VariantStock.tsx';

export type ProductQuickViewProps = {
  api: TypedClient;
  product: Product;
  categoryName: string | null;
  optionIndex: OptionIndex;
  canAddStock: boolean;
  onClose: () => void;
  onEdit: (productId: string) => void;
  onPrintLabels: (productId: string) => void;
  onAdjustStock: ((product: Product) => void) | null;
};

export const ProductQuickView = ({
  api,
  product,
  categoryName,
  optionIndex,
  canAddStock,
  onClose,
  onEdit,
  onPrintLabels,
  onAdjustStock,
}: ProductQuickViewProps) => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const locale = i18n.language;
  const variant = firstVariant(product);
  const cost = auth.hasRole(COST_ROLE) ? (variant?.costPrice ?? null) : null;
  const [adding, setAdding] = useState(false);
  const rows = variantRows(product, optionIndex);

  return (
    <Dialog
      open
      variant="side"
      title={nameIn(product, locale, product.id)}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          {onAdjustStock === null ? null : (
            <Button
              icon={<SlidersHorizontal size={20} aria-hidden="true" />}
              variant="outline"
              onClick={() => {
                onAdjustStock(product);
              }}
            >
              {t('adjustStock.title')}
            </Button>
          )}
          <Button
            icon={<Tags size={20} aria-hidden="true" />}
            variant="outline"
            onClick={() => {
              onPrintLabels(product.id);
            }}
          >
            {t('labels.printLabels')}
          </Button>
          <Button
            icon={<Pencil size={20} aria-hidden="true" />}
            onClick={() => {
              onEdit(product.id);
            }}
          >
            {t('quickView.edit')}
          </Button>
        </>
      }
    >
      <div className={styles['body']}>
        <p className={styles['meta']}>
          <Badge tone={productStatusTone[product.status]}>{t(`catalog.status_${product.status}`)}</Badge>
          <span dir="ltr">{product.reference ?? '—'}</span>
          <span>{categoryName ?? t('quickView.noCategory')}</span>
          {product.createdBy === null ? null : (
            <span>{t('productEditor.createdBy', { name: product.createdBy })}</span>
          )}
        </p>
        <ProductGallery product={product} />
        <PriceTag product={product} size="lg" />
        {cost === null || variant === undefined ? null : (
          <dl className={styles['cost']}>
            <div>
              <dt>{t('productEditor.cost')}</dt>
              <dd>{formatMoney(cost, locale)}</dd>
            </div>
            <div>
              <dt>{t('productEditor.margin')}</dt>
              <dd>{formatMargin(marginOf(variant.price, cost), locale)}</dd>
            </div>
          </dl>
        )}
        <section className={styles['section']} aria-labelledby="quick-view-stock">
          <div className={styles['sectionHead']}>
            <h3 id="quick-view-stock" className={styles['heading']}>
              {t('stockMatrix.caption')}
            </h3>
            {canAddStock ? (
              <Button
                size="sm"
                variant={adding ? 'ghost' : 'outline'}
                icon={<PackagePlus size={18} aria-hidden="true" />}
                aria-expanded={adding}
                onClick={() => {
                  setAdding((current) => !current);
                }}
              >
                {adding ? t('common.cancel') : t('quickStock.title')}
              </Button>
            ) : null}
          </div>
          {adding ? (
            <QuickAddStockForm
              api={api}
              rows={rows}
              onRecorded={() => {
                setAdding(false);
              }}
            />
          ) : null}
          <VariantStock api={api} labelledBy="quick-view-stock" rows={rows} />
        </section>
      </div>
    </Dialog>
  );
};
